import React, { useEffect, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import LoadingAnimation from 'components/loading-animation';
import Table from 'components/table';
import { useGetLoyaltyCardTransactionsQuery } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useLocalizedTransactionTransformer } from 'pages/loyalty/transactions/use-localized-transaction-transformer.th';
import {
  groupAndSortTransactionsByMonth,
  useTransactionDateFormatters
} from 'pages/loyalty/transactions/utils';
import { useCustomizableContent } from 'state/customizable-content';
import { ITable } from 'utils/table';

import { SectionDescription, SectionHeader } from './styled';

const PurchaseAmount = styled.div`
  text-align: right;
`;

export const RewardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: normal;

  text-align: left;
  ${Styles.desktop`
    text-align: right;
  `}
`;

const PurchaseAmountHeader = styled(PurchaseAmount)`
  font-weight: normal;
`;

const TableWrap = styled.div`
  position: relative;
  background-color: ${Styles.color.background};

  border-radius: ${Styles.borderRadius};
  border: 1px solid ${Styles.color.grey.four};

  &:not(:first-of-type) {
    margin-top: 1rem;
  }

  ${Styles.desktop`
    border-radius: ${Styles.borderRadius};
    border: 1px solid ${Styles.color.grey.four};
  `}

  td,
  th {
    font-size: 0.875rem;
    padding: 1rem 0.5rem;

    ${Styles.mobileTiny`
      padding: 1rem 0.25rem;
    `}

    ${Styles.desktop`
      font-size: 1rem;
      padding: 1rem;
    `}
  }
`;

type Transaction = {
  date: string;
  purchaseAmount: React.ReactNode;
  rewards?: React.ReactNode;
};

const TransactionHistory = () => {
  const [tableData, setTableData] = useState<Transaction[]>([]);
  const { activeCard } = useLoyaltyActiveCard();
  const { loyaltyContent } = useCustomizableContent();
  const { formatMessage } = useIntl();
  const { getMonth, formatTransactionDates } = useTransactionDateFormatters();

  const { loading, data: transactionsData } = useGetLoyaltyCardTransactionsQuery({
    variables: {
      cardId: activeCard ? activeCard.cardId : ''
    },
    skip: !activeCard || !activeCard.cardId
  });
  const transactions = useMemo(
    () =>
      transactionsData && transactionsData.getLoyaltyCardTransactions
        ? transactionsData.getLoyaltyCardTransactions.transactions
        : [],
    [transactionsData]
  );

  const transactionTransformer = useLocalizedTransactionTransformer();

  useEffect(() => {
    if (transactions) {
      setTableData(transactions.map(transactionTransformer));
    }
  }, [transactions, transactionTransformer]);

  const tables: ITable<Transaction>[] = useMemo(() => {
    const groupedAndSortedTransactions = groupAndSortTransactionsByMonth(tableData);

    return groupedAndSortedTransactions.map(([, data]) => ({
      head: [
        {
          date: getMonth(data[0]),
          rewards: <RewardsHeader>{formatMessage(messages.rewards)}</RewardsHeader>,
          purchaseAmount: (
            <PurchaseAmountHeader>{formatMessage(messages.purchaseAmount)}</PurchaseAmountHeader>
          )
        }
      ],
      body: formatTransactionDates(data)
    }));
  }, [tableData, formatMessage, formatTransactionDates, getMonth]);

  if (loading) {
    return <LoadingAnimation fillColor={Styles.color.primary} />;
  }

  return (
    <>
      <SectionHeader>{loyaltyContent?.historyPageText?.locale}</SectionHeader>
      <SectionDescription>{loyaltyContent?.historyPageSubtext?.locale}</SectionDescription>
      <SectionDescription>{loyaltyContent?.pointsUpdateText?.locale}</SectionDescription>
      {tables.map(table => (
        <TableWrap key={String(table.head[0].date)}>
          <Table table={table} />
        </TableWrap>
      ))}
    </>
  );
};

export default TransactionHistory;
const messages = defineMessages({
  purchaseAmount: {
    id: 'purchaseAmount',
    defaultMessage: 'Amount',
    description: 'Header text for transaction table'
  },

  rewards: {
    id: 'rewards',
    defaultMessage: 'Rewards',
    description: 'Header text for transaction table'
  }
});
